import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { ArrowUpRight, Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useActiveSection } from '@/hooks/use-active-section'
import { ButtonLink } from '@/components/generals/button-link'
import { SocialLinks } from '@/components/generals/social-links'
import { Wordmark } from '@/components/generals/wordmark'
import { mainNavigation, START_PROJECT_HREF } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

const SECTION_IDS = mainNavigation
    .map(item => item.section)
    .filter((id): id is string => Boolean(id))

const FOCUSABLE = 'a[href], button:not([disabled])'

export const Navbar: FC = (): ReactNode => {
    const router = useRouter()
    const isHome = router.pathname === '/'
    const isWorkPage = router.pathname.startsWith('/work')
    const active = useActiveSection(isHome ? SECTION_IDS : [])

    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    const toggleRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const root = document.documentElement
        const previousOverflow = root.style.overflow
        const desktop = window.matchMedia('(min-width: 1024px)')

        root.style.overflow = 'hidden'
        menuRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus({ preventScroll: true })

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false)
                toggleRef.current?.focus()
                return
            }

            if (event.key !== 'Tab') return

            const items = [
                toggleRef.current,
                ...Array.from(menuRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
            ].filter((item): item is HTMLElement => Boolean(item))

            const first = items[0]
            const last = items[items.length - 1]

            if (!first || !last) return

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            }

            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        const onBreakpoint = () => {
            if (desktop.matches) setOpen(false)
        }

        document.addEventListener('keydown', onKeyDown)
        desktop.addEventListener('change', onBreakpoint)

        return () => {
            root.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKeyDown)
            desktop.removeEventListener('change', onBreakpoint)
        }
    }, [open])

    const closeMenu = () => setOpen(false)

    const isActive = (section?: string): boolean => {
        if (isHome) return Boolean(section) && active === section

        return isWorkPage && section === 'work'
    }

    return (
        <header className='fixed inset-x-0 top-0 z-50'>
            <div
                className={cn(
                    'border-b transition-[background-color,border-color] duration-500',
                    scrolled || open
                        ? 'border-white/[0.07] bg-background/85 backdrop-blur-xl'
                        : 'border-transparent bg-transparent'
                )}
            >
                <nav aria-label='Main' className='mx-auto flex h-18 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                    <Wordmark onClick={closeMenu} />

                    <ul className='hidden items-center gap-1 lg:flex'>
                        {mainNavigation.map(item => {
                            const current = isActive(item.section)

                            return (
                                <li key={item.label}>
                                    <Link
                                        href={item.href}
                                        aria-current={current ? 'location' : undefined}
                                        className={cn(
                                            'relative inline-flex rounded-full px-4 py-2 text-sm outline-none transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-ring',
                                            current ? 'text-white' : 'text-muted-foreground hover:text-white'
                                        )}
                                    >
                                        {item.label}

                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'absolute inset-x-4 -bottom-0.5 h-px origin-left bg-linear-to-r from-electric to-electric-soft transition-transform duration-500 ease-smooth',
                                                current ? 'scale-x-100' : 'scale-x-0'
                                            )}
                                        />
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <div className='flex items-center gap-3'>
                        <ButtonLink
                            href={START_PROJECT_HREF}
                            size='sm'
                            withArrow
                            className='hidden sm:inline-flex'
                        >
                            Start a Project
                        </ButtonLink>

                        <button
                            ref={toggleRef}
                            type='button'
                            onClick={() => setOpen(value => !value)}
                            aria-expanded={open}
                            aria-controls='mobile-menu'
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            className='inline-flex size-11 items-center justify-center rounded-full border border-white/15 text-white outline-none transition-colors duration-300 hover:border-white/35 hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-ring lg:hidden'
                        >
                            {open
                                ? <X aria-hidden='true' className='size-5' />
                                : <Menu aria-hidden='true' className='size-5' />}
                        </button>
                    </div>
                </nav>
            </div>

            <div
                id='mobile-menu'
                ref={menuRef}
                className={cn(
                    'fixed inset-x-0 bottom-0 top-18 z-40 transition-[opacity,visibility] duration-300 lg:hidden',
                    open ? 'visible opacity-100' : 'pointer-events-none invisible opacity-0'
                )}
            >
                <div className='flex h-full flex-col overflow-y-auto bg-background/95 px-5 pb-10 pt-6 backdrop-blur-2xl sm:px-8'>
                    <nav aria-label='Mobile'>
                        <ul>
                            {mainNavigation.map((item, index) => (
                                <li
                                    key={item.label}
                                    className={cn(
                                        'border-b border-white/[0.07] transition-[opacity,transform] duration-500 ease-smooth',
                                        open ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
                                    )}
                                    style={{ transitionDelay: open ? `${80 + index * 50}ms` : '0ms' } as CSSProperties}
                                >
                                    <Link
                                        href={item.href}
                                        onClick={closeMenu}
                                        aria-current={isActive(item.section) ? 'location' : undefined}
                                        className='group flex items-center justify-between gap-4 py-5 outline-none focus-visible:text-electric-soft'
                                    >
                                        <span className='flex items-baseline gap-4'>
                                            <span className='font-mono text-xs text-electric-soft'>
                                                {String(index + 1).padStart(2, '0')}
                                            </span>

                                            <span className='font-display text-[1.75rem] font-extrabold uppercase leading-none tracking-[-0.01em] text-white sm:text-4xl'>
                                                {item.label}
                                            </span>
                                        </span>

                                        <ArrowUpRight aria-hidden='true' className='size-5 text-muted-foreground transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white' />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <ButtonLink
                        href={START_PROJECT_HREF}
                        onClick={closeMenu}
                        size='lg'
                        withArrow
                        className='mt-10 w-full'
                    >
                        Start a Project
                    </ButtonLink>

                    <div className='mt-auto pt-12'>
                        <p className='font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground'>
                            Say hello
                        </p>

                        <a
                            href={`mailto:${siteConfig.email}`}
                            className='mt-3 inline-block break-all text-lg text-white outline-none hover:text-electric-soft focus-visible:text-electric-soft'
                        >
                            {siteConfig.email}
                        </a>

                        <SocialLinks className='mt-6' />
                    </div>
                </div>
            </div>
        </header>
    )
}
